// src/services/dtedocService.js
import { apiClient } from './api';
import { createLogger } from '../utils/logger';

const logger = createLogger('API');

/**
 * Servicio para manejar documentos tributarios electrónicos (XML, PDF) almacenados en el servidor
 */
export const dtedocService = {
  /**
   * Obtiene los documentos asociados a un movimiento RCV
   * @param {number} movimientoId - ID del movimiento RCV
   * @returns {Promise<Array>} - Promesa con los documentos asociados
   */
  async getDocumentosByMovimiento(movimientoId) {
      // Verificar que movimientoId sea válido
    if (!movimientoId) {
      // Retornar un array vacío en lugar de hacer una llamada API que fallará
      logger.warn('Se intentó obtener documentos para un movimiento sin ID');
      return [];
    }
    try {
      const response = await apiClient.get(`/dtedoc/movimiento/${movimientoId}`);
      return response;
    } catch (error) {
      // Si no hay documentos, devolvemos un array vacío en lugar de lanzar error
      if (error.response && error.response.status === 404) {
        console.warn(`No hay documentos asociados al movimiento ${movimientoId}`);
        return [];
      }
      throw error;
    }
  },
  
  /**
   * Obtiene los documentos por RUT, tipo y folio
   * @param {Object} params - Parámetros de búsqueda
   * @returns {Promise<Array>} - Promesa con los documentos encontrados
   */
  async getDocumentosByRutTipoFolio(params) {
    const { rutEmisor, tipoDoc, folio } = params;
    const queryParams = new URLSearchParams({
      rut_emisor: rutEmisor,
      tipo_doc: tipoDoc,
      folio: folio
    }).toString();
    
    return apiClient.get(`/dtedoc/buscar?${queryParams}`);
  },
  
  async getDocumentosByMovimiento(movimientoId) {
    return apiClient.get(`/dtedoc/movimiento/${movimientoId}`);
  },

  /**
   * Obtiene un documento PDF por su ID
   * @param {number} documentoId - ID del documento
   * @returns {Promise<Blob>} - Promesa con el blob del PDF
   */  
  async getDocumentoPdf(documentoId) {
    try {
      return await apiClient.get(`/dtedoc/${documentoId}/pdf`, {
        responseType: 'blob'
      });
    } catch (error) {
      if (error.response && error.response.status === 404) {
        // Documento no encontrado
        console.warn(`Documento PDF con ID ${documentoId} no encontrado`);
        throw new Error('El documento solicitado no existe o no está disponible');
      }
      throw error;
    }
  },

  /**
   * Obtiene un documento XML por su ID
   * @param {number} documentoId - ID del documento
   * @returns {Promise<string>} - Promesa con el contenido del XML
   */
  async getDocumentoXml(documentoId) {
    try {
      return await apiClient.get(`/dtedoc/${documentoId}/xml`, {
        responseType: 'blob'
      });
    } catch (error) {
      if (error.response && error.response.status === 404) {
        console.warn(`Documento XML con ID ${documentoId} no encontrado`);
        throw new Error('El documento XML solicitado no existe o no está disponible');
      }
      throw error;
    }
  },
  
  /**
   * Sube un nuevo documento (PDF o XML)
   * @param {FormData} formData - Datos del formulario incluyendo el archivo
   * @returns {Promise<Object>} - Promesa con la respuesta
   */
  async uploadDocumento(formData) {
    return apiClient.uploadFile('/dtedoc/upload', formData);
  },
  
  /**
   * Asocia un documento a un movimiento RCV
   * @param {number} documentoId - ID del documento
   * @param {number} movimientoId - ID del movimiento RCV
   * @returns {Promise<Object>} - Promesa con la respuesta
   */
  async asociarDocumentoAMovimiento(documentoId, movimientoId) {
    return apiClient.post('/dtedoc/asociar', {
      documento_id: documentoId,
      movimiento_id: movimientoId
    });
  },
  
  /**
   * Descarga un documento
   * @param {number} documentoId - ID del documento
   * @param {string} tipo - Tipo de documento ('pdf' o 'xml')
   * @returns {Promise<void>}
   */
  async downloadDocumento(documentoId, tipo = 'pdf') {
    try {
      // Obtener el blob del documento
      const blob = await apiClient.get(`/dtedoc/${documentoId}/${tipo}`, {
        responseType: 'blob'
      });
      
      // Crear URL para descarga
      const url = window.URL.createObjectURL(blob);
      
      // Crear enlace y simular clic
      const a = document.createElement('a');
      a.href = url;
      a.download = `documento_${documentoId}.${tipo}`;
      document.body.appendChild(a);
      a.click();
      
      // Limpiar
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error(`Error al descargar documento ${tipo}:`, error);
      throw error;
    }
  },
  
  /**
   * Busca documentos por RUT emisor, tipo y folio
   * @param {string} rutEmisor - RUT del emisor
   * @param {string} tipoDoc - Tipo de documento
   * @param {string} folio - Folio del documento
   * @returns {Promise<Array>} - Documentos encontrados
   */
  async buscarDocumentos(rutEmisor, tipoDoc, folio) {
    const queryParams = new URLSearchParams();
    
    if (rutEmisor) queryParams.append('rut_emisor', rutEmisor);
    if (tipoDoc) queryParams.append('tipo_doc', tipoDoc);
    if (folio) queryParams.append('folio', folio);
    
    return apiClient.get(`/dtedoc/buscar?${queryParams.toString()}`);
  },
  
  /**
   * Obtiene información de un documento por su ID
   * @param {number} documentoId - ID del documento
   * @returns {Promise<Object>} - Detalles del documento
   */
  async getDocumento(documentoId) {
    return apiClient.get(`/dtedoc/${documentoId}`);
  },

  /**
   * Busca el PDF de un movimiento RCV en el portal web del SII,
   * lo guarda en el servidor y lo asocia al movimiento.
   * @param {number} movimientoId - ID del movimiento RCV
   * @param {string} rutEmpresa - RUT de la empresa receptora
   * @returns {Promise<Object>} - Documento creado
   */
  async getMovimientosConAdjunto(movimientoIds) {
    if (!movimientoIds || movimientoIds.length === 0) return [];
    return apiClient.post('/dtedoc/con-adjunto', { movimiento_ids: movimientoIds });
  },

  async buscarEnSii(movimientoId, rutEmpresa) {
    return apiClient.post('/dtedoc/buscar-sii', {
      movimiento_id: movimientoId,
      rut_empresa: rutEmpresa,
    });
  },

  async deleteDocumento(documentoId) {
    return apiClient.delete(`/dtedoc/${documentoId}`);
  }
};